/**
 * <GlobalBottomNav/> — renders the BottomNav on every route except a
 * narrow set of TRULY immersive single-purpose audio/visual sessions
 * where any chrome would break the experience.
 *
 * Hide list deliberately small per user feedback: "we need the
 * navigation bar on every page of the site."
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { BottomNav } from "./BottomNav";
import { resolveBrand, isBrandedNarrow } from "../lib/brand";

const IMMERSIVE_PREFIXES = [
  "/golf/coach",
  "/soulumo/from",
  "/soulumo/coach",
  "/atelier/guide",
  "/gym/guide",
  "/networker/guide",
  "/foundations/guide",
  "/lights/guide",
  "/soulumo/guide",
  "/inner-path/guide",
  // Auth / payment flows
  "/login", "/register", "/forgot-password", "/upgrade", "/payment",
  // Public listen / gift links
  "/listen", "/gift", "/gift-moment", "/share/audio",
  // Brand preview / mood boards — shareable, no chrome
  "/brand", "/brand-preview",
  // Wizards / onboarding
  "/networker/performance-vision/setup",
  "/networker/onboarding",
  // True immersive session players (audio + single-orb / dot screens):
  "/practices/state-tuning/play",
  "/practices/tapping/play",
  "/networker/prime/session",
  "/playback", "/immersion",
  "/abundance/living-vision-board",
  "/integration-program/session",
  // Your Living Dream — Phase 2 cinematic surfaces (arrival/write/result/board)
  "/living-dream",
  // The Reclaim Atelier — ultra-premium tier (hub, write, dashboard, programs)
  "/atelier",
  "/gift/atelier",
  // Collective Alignment public share preview — clean conversion page
  "/share/alignment",
  // Rewrite Engine — all 6 domain rituals + hub
  "/rewrite",
  "/golf/reclaim-the-shot",
  "/clearing/reclaim-the-pattern",
  "/abundance/rewrite-scarcity",
  "/presence/return-to-here",
  "/relationships/rewrite-the-armour",
  "/performance/free-under-pressure",
  "/inner-path/deep/",
  "/inner-path/daily/",
  "/inner-path/morning-arrival",
  "/heart-ru",
  "/spine",
  "/heart/dad",
  // Soulumo member pages carry their own tab bar
  "/my-installations", "/my-journeys", "/my-library", "/signature-installations", "/voice-library", "/soulumo/account",
];

export default function GlobalBottomNav() {
  const { pathname } = useLocation();
  const [immersiveBodyFlag, setImmersiveBodyFlag] = React.useState(
    typeof document !== 'undefined' && document.body.dataset.immersive === 'true'
  );

  // Watch body.dataset.immersive — set imperatively by useImmersiveMode hook
  // when a same-URL overlay (e.g. Bank of Reclaim ritual) takes over the screen.
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const obs = new MutationObserver(() => {
      setImmersiveBodyFlag(document.body.dataset.immersive === 'true');
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-immersive'] });
    return () => obs.disconnect();
  }, []);

  const hide = immersiveBodyFlag || IMMERSIVE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname === p);
  if (hide) return null;
  // Mar 3 2026 · When the app is served from a branded hostname
  // (soulumo.app / onemillionlights.org) we hide the full Reclaim
  // bottom nav so the front door feels like a stand-alone product /
  // movement, not a slice of a bigger ecosystem.
  if (isBrandedNarrow(resolveBrand())) return null;
  return <BottomNav />;
}
